import React from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { MONTH_NAMES } from '../../../utils/projectAnalysisPeriod'
import { ExportMenuButton } from '../../common/export-menu-button'
import type { EmployeeDetailPageModel } from '../../../hooks/use-employee-detail-page'

const COLORS = [
  '#3b82f6',
  '#22c55e',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#14b8a6',
]

function KpiTile({
  title,
  value,
  subtitle,
}: {
  title: string
  value: string
  subtitle?: string
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
      <p className="text-[10px] font-medium text-slate-500">{title}</p>
      <p className="text-sm font-bold text-slate-900 mt-0.5">{value}</p>
      {subtitle && (
        <p className="text-[10px] text-slate-500 mt-0 truncate">{subtitle}</p>
      )}
    </div>
  )
}

export interface EmployeeDetailTabContentProps {
  m: EmployeeDetailPageModel
}

/** Tab bodies only — header + tab bar live in `Page.tsx` (project-detail pattern). */
export function EmployeeDetailTabContent({ m }: EmployeeDetailTabContentProps) {
  const userStats = m.userStats
  if (!userStats) return null

  return (
    <div className="space-y-3">
      {m.activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <KpiTile
              title="Total hours"
              value={userStats.totalHours.toLocaleString(undefined, {
                maximumFractionDigits: 1,
              })}
              subtitle={`${userStats.monthlyData.length} months`}
            />
            <KpiTile
              title="Billable hours"
              value={userStats.billableHours.toLocaleString(undefined, {
                maximumFractionDigits: 1,
              })}
              subtitle={`${userStats.utilizationRate.toFixed(1)}% util`}
            />
            <KpiTile
              title="Active days"
              value={userStats.totalActiveDays.toLocaleString()}
              subtitle={`${userStats.totalEntries.toLocaleString()} entries`}
            />
            <KpiTile
              title="Late %"
              value={
                userStats.lateEntriesRate != null
                  ? `${userStats.lateEntriesRate.toFixed(1)}%`
                  : '—'
              }
              subtitle="Late / total entries"
            />
            <KpiTile
              title="Projects"
              value={String(userStats.uniqueProjects)}
              subtitle="Projects worked"
            />
            <KpiTile
              title="Leaves"
              value={
                userStats.totalLeaveDays > 0
                  ? userStats.totalLeaveDays.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })
                  : '0'
              }
              subtitle={`Full: ${(userStats.fullDayLeaves ?? 0).toLocaleString()} · Half: ${(userStats.halfDayLeaves ?? 0).toLocaleString()}`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Hours distribution
              </h3>
              {userStats.totalHours > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={m.pieChartData.filter((d) => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={44}
                      outerRadius={72}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {m.pieChartData
                        .filter((d) => d.value > 0)
                        .map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) =>
                        `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} hrs`
                      }
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[220px] flex items-center justify-center text-sm text-slate-500">
                  No hours for selected period
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Utilization rate
              </h3>
              <div className="flex flex-col items-center justify-center h-[220px]">
                <div className="relative w-36 h-36">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 72 72">
                    <circle
                      cx="36"
                      cy="36"
                      r="30"
                      stroke="#e5e7eb"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="36"
                      cy="36"
                      r="30"
                      stroke={
                        userStats.utilizationRate >= 70
                          ? '#22c55e'
                          : userStats.utilizationRate >= 50
                            ? '#f59e0b'
                            : '#ef4444'
                      }
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${(userStats.utilizationRate / 100) * 188.5} 188.5`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-slate-900">
                      {userStats.utilizationRate.toFixed(0)}%
                    </span>
                    <span className="text-xs text-slate-500">Billable</span>
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-500 text-center">
                  {userStats.billableHours.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })}{' '}
                  billable of{' '}
                  {userStats.totalHours.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })}{' '}
                  total hours
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Top projects by hours
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={userStats.projectsData.slice(0, 8).map((p) => ({
                  ...p,
                  project_name: p.project_name || `Project ${p.project_id}`,
                  billable_hours: Math.round(p.billable_hours * 100) / 100,
                  non_billable_hours: Math.round(p.non_billable_hours * 100) / 100,
                  total_hours: Math.round(p.total_hours * 100) / 100,
                }))}
                layout="vertical"
                margin={{ top: 8, right: 24, left: 4, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={m.chartAxis.grid} />
                <XAxis
                  type="number"
                  stroke={m.chartAxis.tick}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })
                  }
                />
                <YAxis
                  type="category"
                  dataKey="project_name"
                  stroke={m.chartAxis.tick}
                  tick={{ fontSize: 11 }}
                  width={180}
                  interval={0}
                  tickMargin={4}
                />
                <Tooltip />
                <Legend />
                <Bar dataKey="billable_hours" name="Billable" fill="#22c55e" stackId="a" />
                <Bar
                  dataKey="non_billable_hours"
                  name="Non-Billable"
                  fill="#ef4444"
                  stackId="a"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {m.activeTab === 'trends' && (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Monthly hours trend
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={m.monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={m.chartAxis.grid} />
                <XAxis
                  dataKey="name"
                  stroke={m.chartAxis.tick}
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  yAxisId="left"
                  stroke={m.chartAxis.tick}
                  label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={m.chartAxis.tick}
                  label={{
                    value: 'Utilization %',
                    angle: 90,
                    position: 'insideRight',
                  }}
                />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="billableHours" name="Billable" fill="#22c55e" />
                <Bar
                  yAxisId="left"
                  dataKey="nonBillableHours"
                  name="Non-Billable"
                  fill="#ef4444"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="utilizationRate"
                  name="Utilization %"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6' }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="lateSubmissionPct"
                  name="Late submission %"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b' }}
                  strokeDasharray="4 4"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Late submission % over time
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={m.monthlyChartData}>
                <defs>
                  <linearGradient id={m.gidLate} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={m.chartAxis.grid} />
                <XAxis
                  dataKey="name"
                  stroke={m.chartAxis.tick}
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  domain={[0, 100]}
                  stroke={m.chartAxis.tick}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  formatter={(value: number) => [`${value.toFixed(1)}%`, 'Late %']}
                />
                <Area
                  type="monotone"
                  dataKey="lateSubmissionPct"
                  name="Late submission %"
                  stroke="#f59e0b"
                  fillOpacity={1}
                  fill={`url(#${m.gidLate})`}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Utilization over time
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={m.monthlyChartData}>
                <defs>
                  <linearGradient id={m.gidUtil} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={m.chartAxis.grid} />
                <XAxis
                  dataKey="name"
                  stroke={m.chartAxis.tick}
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  domain={[0, 100]}
                  stroke={m.chartAxis.tick}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                <Area
                  type="monotone"
                  dataKey="utilizationRate"
                  name="Utilization rate"
                  stroke="#3b82f6"
                  fillOpacity={1}
                  fill={`url(#${m.gidUtil})`}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {m.activeTab === 'projects' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-md overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              Projects ({userStats.projectsData.length}) — click a row for monthly
              breakdown
            </h3>
            <ExportMenuButton
              disabled={!userStats.projectsData.length}
              onCsv={m.onExportProjectsCsv}
              onExcel={m.onExportProjectsExcel}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500">
                    Project
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                    Total hrs
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                    Billable
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                    Non-bill.
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                    Util.
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500">
                    Dist.
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {userStats.projectsData.map((proj, idx) => {
                  const util =
                    proj.total_hours > 0
                      ? (proj.billable_hours / proj.total_hours) * 100
                      : 0
                  const totalPercent =
                    userStats.totalHours > 0
                      ? (proj.total_hours / userStats.totalHours) * 100
                      : 0
                  const isSelected = m.selectedProjectId === proj.project_id
                  const monthlyBreakdown = isSelected
                    ? userStats.monthlyData
                        .map((month) => {
                          const p = month.projects_breakdown?.find(
                            (b) => b.project_id === proj.project_id,
                          )
                          if (!p) return null
                          const total = parseFloat(String(p.total_hours || 0))
                          const billable = parseFloat(String(p.billable_hours || 0))
                          const utilPct = total > 0 ? (billable / total) * 100 : 0
                          return {
                            year: month.year,
                            month: month.month,
                            name: `${month.year}-${String(month.month).padStart(2, '0')}`,
                            totalHours: total,
                            billableHours: billable,
                            nonBillableHours: parseFloat(
                              String(p.non_billable_hours || 0),
                            ),
                            util: utilPct,
                          }
                        })
                        .filter(Boolean)
                        .sort((a, b) =>
                          a!.year !== b!.year ? a!.year - b!.year : a!.month - b!.month,
                        )
                    : []
                  const sumTotal = monthlyBreakdown.reduce(
                    (s, r) => s + r!.totalHours,
                    0,
                  )
                  const sumBillable = monthlyBreakdown.reduce(
                    (s, r) => s + r!.billableHours,
                    0,
                  )
                  const sumNonBillable = monthlyBreakdown.reduce(
                    (s, r) => s + r!.nonBillableHours,
                    0,
                  )
                  const totalUtilPct =
                    sumTotal > 0 ? (sumBillable / sumTotal) * 100 : 0

                  return (
                    <React.Fragment key={proj.project_id}>
                      <tr
                        ref={(el) => {
                          if (isSelected && el) m.selectedProjectRowRef.current = el
                        }}
                        onClick={() =>
                          m.setSelectedProjectId(isSelected ? null : proj.project_id)
                        }
                        className={`cursor-pointer ${
                          isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded flex items-center justify-center text-white font-bold text-[10px]"
                              style={{
                                backgroundColor: COLORS[idx % COLORS.length],
                              }}
                            >
                              {(proj.project_name || 'P').charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-slate-900 truncate max-w-[200px]">
                                {proj.project_name || `Project ${proj.project_id}`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-xs font-medium text-slate-900 tabular-nums">
                          {Number(proj.total_hours).toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-3 py-2 text-right text-xs text-emerald-600 tabular-nums">
                          {Number(proj.billable_hours).toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-3 py-2 text-right text-xs text-red-600 tabular-nums">
                          {Number(proj.non_billable_hours).toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              util >= 70
                                ? 'bg-green-100 text-green-800'
                                : util >= 50
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {util.toFixed(0)}%
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <div className="w-16 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${totalPercent}%`,
                                  backgroundColor: COLORS[idx % COLORS.length],
                                }}
                              />
                            </div>
                            <span className="text-[10px] text-slate-500">
                              {totalPercent.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                      {isSelected && (
                        <tr>
                          <td colSpan={6} className="p-0 align-top bg-slate-50/80">
                            <div
                              ref={m.breakdownSectionRef}
                              className="border-t border-slate-200"
                            >
                              <div className="px-4 py-2 flex items-center justify-between bg-slate-100/80">
                                <h4 className="text-sm font-semibold text-slate-900">
                                  Breakdown —{' '}
                                  {proj.project_name || `Project ${proj.project_id}`}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => {
                                    m.selectedProjectRowRef.current?.scrollIntoView({
                                      behavior: 'smooth',
                                      block: 'center',
                                    })
                                    m.setSelectedProjectId(null)
                                  }}
                                  className="text-xs font-medium px-2 py-1 rounded bg-slate-200 text-slate-700 hover:bg-slate-300"
                                >
                                  Close
                                </button>
                              </div>
                              <div className="overflow-x-auto px-4 pb-3">
                                <table className="min-w-full divide-y divide-slate-200">
                                  <thead className="bg-slate-100">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500">
                                        Period
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                                        Total hrs
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                                        Billable
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                                        Non-bill.
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                                        Util. %
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-200">
                                    {monthlyBreakdown.map((row) => (
                                      <tr key={row!.name} className="hover:bg-white">
                                        <td className="px-3 py-2 text-xs font-medium text-slate-900">
                                          {MONTH_NAMES[row!.month - 1]} {row!.year}
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                                          {row!.totalHours.toLocaleString(undefined, {
                                            maximumFractionDigits: 2,
                                          })}
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs text-emerald-600 tabular-nums">
                                          {row!.billableHours.toLocaleString(undefined, {
                                            maximumFractionDigits: 2,
                                          })}
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs text-red-600 tabular-nums">
                                          {row!.nonBillableHours.toLocaleString(
                                            undefined,
                                            { maximumFractionDigits: 2 },
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          <span
                                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                              row!.util >= 70
                                                ? 'bg-green-100 text-green-800'
                                                : row!.util >= 50
                                                  ? 'bg-amber-100 text-amber-800'
                                                  : 'bg-red-100 text-red-800'
                                            }`}
                                          >
                                            {row!.util.toFixed(0)}%
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                    <tr className="font-semibold bg-slate-100">
                                      <td className="px-3 py-2 text-xs text-slate-900">
                                        Total
                                      </td>
                                      <td className="px-3 py-2 text-right text-xs text-slate-900 tabular-nums">
                                        {sumTotal.toLocaleString(undefined, {
                                          maximumFractionDigits: 2,
                                        })}
                                      </td>
                                      <td className="px-3 py-2 text-right text-xs text-emerald-700 tabular-nums">
                                        {sumBillable.toLocaleString(undefined, {
                                          maximumFractionDigits: 2,
                                        })}
                                      </td>
                                      <td className="px-3 py-2 text-right text-xs text-red-700 tabular-nums">
                                        {sumNonBillable.toLocaleString(undefined, {
                                          maximumFractionDigits: 2,
                                        })}
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        <span
                                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            totalUtilPct >= 70
                                              ? 'bg-green-100 text-green-800'
                                              : totalUtilPct >= 50
                                                ? 'bg-amber-100 text-amber-800'
                                                : 'bg-red-100 text-red-800'
                                          }`}
                                        >
                                          {totalUtilPct.toFixed(0)}%
                                        </span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
                <tr className="font-semibold bg-slate-100">
                  <td className="px-3 py-2 text-xs text-slate-900">Total</td>
                  <td className="px-3 py-2 text-right text-xs text-slate-900 tabular-nums">
                    {userStats.projectsData
                      .reduce((s, p) => s + Number(p.total_hours || 0), 0)
                      .toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-emerald-700 tabular-nums">
                    {userStats.projectsData
                      .reduce((s, p) => s + Number(p.billable_hours || 0), 0)
                      .toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-red-700 tabular-nums">
                    {userStats.projectsData
                      .reduce((s, p) => s + Number(p.non_billable_hours || 0), 0)
                      .toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {(() => {
                      const sumT = userStats.projectsData.reduce(
                        (s, p) => s + Number(p.total_hours || 0),
                        0,
                      )
                      const sumB = userStats.projectsData.reduce(
                        (s, p) => s + Number(p.billable_hours || 0),
                        0,
                      )
                      const pct = sumT > 0 ? (sumB / sumT) * 100 : 0
                      return (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            pct >= 70
                              ? 'bg-green-100 text-green-800'
                              : pct >= 50
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {pct.toFixed(0)}%
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-3 py-2 text-right text-[10px] text-slate-500">
                    100%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {m.activeTab === 'details' && (() => {
        const detailsYears = [
          ...new Set(userStats.monthlyData.map((d) => d.year)),
        ].sort((a, b) => b - a)
        const filteredDetailsData = userStats.monthlyData.filter((month) => {
          const yearMatch =
            m.detailsYearFilter === 'all' ||
            month.year === parseInt(m.detailsYearFilter, 10)
          const monthMatch =
            m.detailsMonthFilter === 'all' ||
            month.month === parseInt(m.detailsMonthFilter, 10)
          return yearMatch && monthMatch
        })

        return (
          <div className="rounded-xl border border-slate-200 bg-white shadow-md overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <h3 className="text-sm font-semibold text-slate-900">
                Monthly details ({filteredDetailsData.length} of{' '}
                {userStats.monthlyData.length})
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                <ExportMenuButton
                  disabled={!filteredDetailsData.length}
                  onCsv={m.onExportDetailsCsv}
                  onExcel={m.onExportDetailsExcel}
                />
                <label className="text-xs text-slate-500">Year</label>
                <select
                  value={m.detailsYearFilter}
                  onChange={(e) => m.setDetailsYearFilter(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1"
                >
                  <option value="all">All years</option>
                  {detailsYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <label className="text-xs text-slate-500">Month</label>
                <select
                  value={m.detailsMonthFilter}
                  onChange={(e) => m.setDetailsMonthFilter(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1"
                >
                  <option value="all">All months</option>
                  {MONTH_NAMES.map((name, idx) => (
                    <option key={name} value={idx + 1}>
                      {name}
                    </option>
                  ))}
                </select>
                {(m.detailsYearFilter !== 'all' || m.detailsMonthFilter !== 'all') && (
                  <button
                    type="button"
                    onClick={() => {
                      m.setDetailsYearFilter('all')
                      m.setDetailsMonthFilter('all')
                    }}
                    className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500">
                      Period
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                      Total hrs
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                      Billable
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                      Util.
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                      Active days
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                      Projects
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                      Full-day leave
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                      Half-day leave
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                      Total leave
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500">
                      Late
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredDetailsData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-3 py-6 text-center text-xs text-slate-500"
                      >
                        No data for selected filters
                      </td>
                    </tr>
                  ) : (
                    [...filteredDetailsData]
                      .sort((a, b) => {
                        if (b.year !== a.year) return b.year - a.year
                        return b.month - a.month
                      })
                      .map((month) => {
                        const util = parseFloat(String(month.billable_percentage || 0))
                        return (
                          <tr key={`${month.year}-${month.month}`} className="hover:bg-slate-50">
                            <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-slate-900">
                              {MONTH_NAMES[month.month - 1]} {month.year}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                              {parseFloat(String(month.total_hours)).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 1 },
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-emerald-600 tabular-nums">
                              {parseFloat(String(month.billable_hours)).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 1 },
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span
                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  util >= 70
                                    ? 'bg-green-100 text-green-800'
                                    : util >= 50
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {util.toFixed(0)}%
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-slate-600">
                              {month.active_days != null ? month.active_days : '—'}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-slate-600">
                              {month.projects_worked}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                              {parseFloat(String(month.full_day_leaves || 0)).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 0 },
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                              {parseFloat(String(month.half_day_leaves || 0)).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 0 },
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                              {parseFloat(String(month.total_leave_days || 0)).toLocaleString(
                                undefined,
                                { maximumFractionDigits: 1 },
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span
                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  parseInt(String(month.late_entries_count || 0), 10) ===
                                  0
                                    ? 'bg-green-100 text-green-800'
                                    : parseInt(
                                          String(month.late_entries_count || 0),
                                          10,
                                        ) <= 5
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {month.late_entries_count}
                              </span>
                            </td>
                          </tr>
                        )
                      })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      })()}

    </div>
  )
}
